<template>
    <a-config-provider :locale="ptBR">
        <div class="map-wrapper">
            <div id="map" class="map"></div>

            <div class="map-layer-switch">
                <MapLayerSwitcher :map="mapRef" v-if="!!mapRef" />
            </div>

            <div v-if="mapDateFilterLoading" class="map-loading-overlay" aria-live="polite" aria-busy="true">
                <a-spin size="large" tip="Carregando mapa..." />
            </div>

        <!-- Legenda de operações (auditoria) – canto inferior esquerdo, só após clicar em Aplicar -->
        <a-card
            v-if="showAuditLegend"
            class="audit-legend"
            size="small"
            :bordered="true"
        >
            <div class="panel-header">
                <span class="title">Legenda</span>
            </div>
            <div class="audit-legend-items">
                <div class="audit-legend-item">
                    <span class="audit-legend-color" :style="{ backgroundColor: ASOF_YELLOW.fill, borderColor: ASOF_YELLOW.stroke }"></span>
                    <span>Como era na data</span>
                </div>
                <div class="audit-legend-item">
                    <span class="audit-legend-color" :style="{ backgroundColor: AUDIT_OP_COLORS.I.fill, borderColor: AUDIT_OP_COLORS.I.stroke }"></span>
                    <span>Inclusão</span>
                </div>
                <div class="audit-legend-item">
                    <span class="audit-legend-color" :style="{ backgroundColor: AUDIT_OP_COLORS.U.fill, borderColor: AUDIT_OP_COLORS.U.stroke }"></span>
                    <span>Alteração</span>
                </div>
                <div class="audit-legend-item">
                    <span class="audit-legend-color" :style="{ backgroundColor: AUDIT_OP_COLORS.D.fill, borderColor: AUDIT_OP_COLORS.D.stroke }"></span>
                    <span>Exclusão</span>
                </div>
            </div>
        </a-card>

        <!-- Painel "Como na data": aparece se a camada tiver histórico em audit.layers_edit_audits -->
        <a-card
            v-if="hasAudit"
            class="filter-panel date-filter-panel"
            size="small"
            :bordered="true"
        >
            <div class="panel-header">
                <span class="title">Visualizar como na data</span>
            </div>
            <div class="date-filter-row">
                <a-date-picker
                    v-model:value="filterDate"
                    value-format="YYYY-MM-DD"
                    format="DD/MM/YYYY"
                    placeholder="Selecione a data"
                    style="width: 100%"
                />
                <a-space class="date-actions">
                    <a-button type="primary" size="small" :loading="applyingDate" @click="applyDateFilter">
                        Aplicar
                    </a-button>
                    <a-button size="small" :disabled="!filterDate" @click="clearDateFilter">
                        Estado atual
                    </a-button>
                </a-space>
            </div>
            <div v-if="filterDate" class="muted small">
                Amarelo: geometria como era na data. Cores (verde/azul/cinza): alterações no dia {{ formatDisplayDate(filterDate) }}.
            </div>
        </a-card>

        <!-- Painel de filtro dinâmico: só aparece se a camada for vetorial -->
        <a-card
            v-if="isVectorLayerFlag"
            class="filter-panel"
            size="small"
            :bordered="true"
        >
            <div class="panel-header">
                <span class="title">Filtro do Layer</span>
                <a-tag v-if="matchCount !== null" color="blue">{{ matchCount }} itens</a-tag>
            </div>

            <div v-if="fields.length === 0" class="muted">
                Carregando campos do layer...
            </div>

            <template v-else>
                <div v-for="(row, idx) in filterRows" :key="row.id" class="filter-row">
                    <a-space :size="8" wrap>
                        <a-select
                            v-model:value="row.field"
                            :options="fieldOptions"
                            placeholder="Campo"
                            style="min-width: 180px"
                            @change="onFieldChange(idx)"
                        />
                        <a-select
                            v-model:value="row.op"
                            :options="operatorOptionsFor(idx)"
                            placeholder="Operador"
                            style="min-width: 150px"
                        />

                        <!-- Valor (texto / número / data / entre) -->
                        <template v-if="needsSingleInput(row)">
                            <a-input
                                v-if="fieldTypeOf(row.field) === 'string'"
                                v-model:value="row.value"
                                placeholder="Valor"
                                style="min-width: 180px"
                            />
                            <a-input-number
                                v-else-if="fieldTypeOf(row.field) === 'number'"
                                v-model:value="row.value"
                                placeholder="Valor"
                                :controls="false"
                                style="min-width: 160px; width: 160px"
                            />
                            <a-date-picker
                                v-else-if="fieldTypeOf(row.field) === 'date'"
                                v-model:value="row.value"
                                value-format="YYYY-MM-DD"
                                placeholder="Data"
                                style="min-width: 160px; width: 160px"
                            />
                        </template>

                        <template v-else-if="needsBetween(row)">
                            <a-space :size="6">
                                <a-input-number
                                    v-if="fieldTypeOf(row.field) === 'number'"
                                    v-model:value="row.valueMin"
                                    placeholder="Mín"
                                    :controls="false"
                                    style="width: 120px"
                                />
                                <a-input-number
                                    v-if="fieldTypeOf(row.field) === 'number'"
                                    v-model:value="row.valueMax"
                                    placeholder="Máx"
                                    :controls="false"
                                    style="width: 120px"
                                />

                                <a-date-picker
                                    v-if="fieldTypeOf(row.field) === 'date'"
                                    v-model:value="row.valueMin"
                                    value-format="YYYY-MM-DD"
                                    placeholder="Início"
                                    style="width: 140px"
                                />
                                <a-date-picker
                                    v-if="fieldTypeOf(row.field) === 'date'"
                                    v-model:value="row.valueMax"
                                    value-format="YYYY-MM-DD"
                                    placeholder="Fim"
                                    style="width: 140px"
                                />
                            </a-space>
                        </template>

                        <a-button type="text" danger @click="removeRow(idx)">
                            Remover
                        </a-button>
                    </a-space>
                </div>

                <div class="panel-actions">
                    <a-space>
                        <a-button @click="addRow">+ Regra</a-button>
                        <a-button type="primary" @click="applyFilter" :loading="applying">Aplicar</a-button>
                        <a-button @click="clearFilter" :disabled="!hasAnyFilter">Limpar</a-button>
                    </a-space>
                </div>
            </template>
        </a-card>
        </div>

        <HtmlModalMap ref="modalRef" @hide="closePopover" />
    </a-config-provider>
</template>

<script setup>
import { onMounted, onUnmounted, ref, computed, nextTick } from 'vue'
import ptBR from 'ant-design-vue/es/locale/pt_BR'
import axios from 'axios'
import Map from 'ol/Map'
import View from 'ol/View'
import { baseLayers } from '../../../open-layers/baseLayers'
import { defaults as defaultControlsOL } from 'ol/control/defaults'
import { fullScreenBtn } from '../../../open-layers/default-controls'
import { getDefaultMapView } from '../../../open-layers/map-defaults.js'
import { addLayerToMapAndSwitcher, removeLayerFromMapAndSwitcher, applyViewerLayerSwitcherDefaults, buildStyleFunctionFromBackend, createLayerToViewer, fetchAllFeatures, mapsLayerTypeValue } from '../../../open-layers/helpers'
import { geoJsonReadOptions } from '../../../open-layers/projections.js'
import { getCenter } from 'ol/extent'
import { toLonLat } from 'ol/proj'
import HtmlModalMap from '../MapsOpenLayers/Components/HtmlModalMap.vue'
import Select from 'ol/interaction/Select'

// Estilos fallback (quando a camada não tem style explícito)
import Style from 'ol/style/Style'
import Fill from 'ol/style/Fill'
import Stroke from 'ol/style/Stroke'
import CircleStyle from 'ol/style/Circle'

// Para checar se é vetorial
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
// (mantemos importáveis caso no futuro precise tratar)
import VectorTileLayer from 'ol/layer/VectorTile'
import VectorTileSource from 'ol/source/VectorTile'
import GeoJSON from 'ol/format/GeoJSON'
import ImageLayer from 'ol/layer/Image'
import TileLayer from 'ol/layer/Tile'
import MapLayerSwitcher from '../MapsOpenLayers/Components/MapLayerSwitcher.vue'

const props = defineProps({
    id: { type: [Number, String], required: true },
    isThematic: { type: Boolean, default: false },
})

const layerId = computed(() => props.id)
const isThematicLayer = computed(() => props.isThematic)

const activePopoverCoordinate = ref(null)
const modalRef = ref(null)
const mapRef = ref(null)
const olLayerRef = ref(null)

// Flag para mostrar/ocultar painel de filtro
const isVectorLayerFlag = ref(false)

// ======= Filtro por data (auditoria: mostrar mapa como na data) =======
const layerConfigRef = ref(null)
const hasAudit = ref(false)
const filterDate = ref(null)
const applyingDate = ref(false)
const mapDateFilterLoading = ref(false)
const showAuditLegend = ref(false)
let dateFilterRun = 0
/** Camada "na data" (feições alteradas desde a data consultada) */
const asOfVectorLayerRef = ref(null)
/** Camada de auditoria (I/U/D no dia consultado) */
const auditVectorLayerRef = ref(null)
/** Select interaction na camada de auditoria */
const auditSelectInteractionRef = ref(null)
/** Handler de clique na camada "na data" */
let asOfClickHandler = null
/** Tabela geo usada no filtro por data (para histórico ao clicar) */
const dateFilterTableNameRef = ref(null)
/** Visibilidade da camada base antes do filtro por data (restaurada ao limpar). */
let baseLayerVisibleBeforeDateFilter = null
const dateFilterOverlayLayers = []

// ======= Estado do filtro dinâmico =======
const fields = ref([]) // [{ key, label, type }]
const fieldOptions = computed(() => fields.value.map(f => ({ label: `${f.label} (${f.type})`, value: f.key })))
const filterRows = ref([])
const applying = ref(false)
const matchCount = ref(null)

const hasAnyFilter = computed(() => {
    return filterRows.value.some(r =>
        r.field &&
        (needsSingleInput(r) ? (r.value ?? '').toString().length
                : needsBetween(r) ? (r.valueMin != null && r.valueMax != null)
                    : true
        ) || isEmptyOp(r)
    )
})

const TEXT_OPS = [
    { value: 'contains', label: 'contém' },
    { value: 'not_contains', label: 'não contém' },
    { value: 'eq', label: 'igual' },
    { value: 'starts', label: 'começa com' },
    { value: 'ends', label: 'termina com' },
    { value: 'empty', label: 'vazio' },
    { value: 'not_empty', label: 'não vazio' },
]
const NUMBER_OPS = [
    { value: 'eq', label: '=' },
    { value: 'neq', label: '≠' },
    { value: 'gt', label: '>' },
    { value: 'gte', label: '≥' },
    { value: 'lt', label: '<' },
    { value: 'lte', label: '≤' },
    { value: 'between', label: 'entre' },
]
const DATE_OPS = [
    { value: 'on', label: 'em' },
    { value: 'before', label: 'antes de' },
    { value: 'after', label: 'depois de' },
    { value: 'between', label: 'entre' },
]

function operatorOptionsFor(idx) {
    const row = filterRows.value[idx]
    const t = fieldTypeOf(row.field)
    if (t === 'string') return TEXT_OPS
    if (t === 'number') return NUMBER_OPS
    if (t === 'date') return DATE_OPS
    return []
}

function addRow() {
    filterRows.value.push({
        id: cryptoRandomId(),
        field: fields.value[0]?.key ?? null,
        op: defaultOpFor(fields.value[0]?.type),
        value: null,
        valueMin: null,
        valueMax: null,
    })
}
function removeRow(idx) { filterRows.value.splice(idx, 1) }
function onFieldChange(idx) {
    const row = filterRows.value[idx]
    row.op = defaultOpFor(fieldTypeOf(row.field))
    row.value = null
    row.valueMin = null
    row.valueMax = null
}
function defaultOpFor(t) {
    if (t === 'string') return 'contains'
    if (t === 'number') return 'eq'
    if (t === 'date') return 'on'
    return 'contains'
}
function fieldTypeOf(key) {
    return fields.value.find(f => f.key === key)?.type || 'string'
}
function needsBetween(row) { return row.field && (row.op === 'between') }
function needsSingleInput(row) { if (!row.field) return false; if (isEmptyOp(row)) return false; return !needsBetween(row) }
function isEmptyOp(row) { return row.op === 'empty' || row.op === 'not_empty' }
function cryptoRandomId() {
    try { return crypto.getRandomValues(new Uint32Array(1))[0].toString(16) }
    catch { return Math.random().toString(16).slice(2) }
}

// ======= Filtro por data: aplicar / limpar =======
function formatDisplayDate(isoDate) {
    if (!isoDate) return ''
    const d = new Date(isoDate + 'T12:00:00')
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function createAuditOverlayVectorLayer({ source, styleFn, label, baseLayer, zIndexOffset, displayInLayerSwitcher = true }) {
    const baseName = baseLayer?.get?.('name') || baseLayer?.get?.('title') || 'Camada'
    const title = `${baseName} (${label})`
    const layer = new VectorLayer({
        source,
        style: styleFn,
        visible: true,
        zIndex: (baseLayer?.getZIndex?.() ?? 0) + zIndexOffset,
        displayInLayerSwitcher,
    })
    layer.set('name', title)
    layer.set('title', title)
    layer.set('dateFilterAuditOverlay', true)
    return layer
}

function addOverlayLayerToMap(layer, layerRef) {
    const map = mapRef.value
    if (!map || !layer) return false
    layerRef.value = layer
    const added = addLayerToMapAndSwitcher(map, layer)
    if (added && !dateFilterOverlayLayers.includes(layer)) {
        dateFilterOverlayLayers.push(layer)
    }
    return added
}

function removeDateFilterOverlay() {
    const map = mapRef.value
    if (!map) return

    [
        ...dateFilterOverlayLayers,
        asOfVectorLayerRef.value,
        auditVectorLayerRef.value,
        ...map.getLayers().getArray().filter((layer) => layer.get?.('dateFilterAuditOverlay')),
    ].filter((layer, index, layers) => layer && layers.indexOf(layer) === index)
        .forEach((layer) => {
            layer.getSource()?.clear?.()
            removeLayerFromMapAndSwitcher(map, layer)
        })

    dateFilterOverlayLayers.splice(0)
    asOfVectorLayerRef.value = null
    auditVectorLayerRef.value = null

    if (asOfClickHandler) {
        map.un('singleclick', asOfClickHandler)
        asOfClickHandler = null
    }
    if (auditSelectInteractionRef.value) {
        map.removeInteraction(auditSelectInteractionRef.value)
        auditSelectInteractionRef.value = null
    }
    dateFilterTableNameRef.value = null
    showAuditLegend.value = false
    modalRef.value?.closePopover?.()
    activePopoverCoordinate.value = null
}

function getAsOfFeatureAtPixel(map, pixel) {
    const layer = asOfVectorLayerRef.value
    if (!map || !layer?.getVisible?.()) return null
    return map.forEachFeatureAtPixel(
        pixel,
        (feature, hitLayer) => (hitLayer === layer ? feature : undefined),
        { hitTolerance: 8, layerFilter: (hitLayer) => hitLayer === layer },
    ) ?? null
}

const ASOF_YELLOW = { fill: 'rgba(255, 193, 7, 0.25)', stroke: '#d39e00' }
function asOfStyleFn(/* feature */) {
    // camada "na data" – amarelo
    return new Style({
        fill: new Fill({ color: ASOF_YELLOW.fill }),
        stroke: new Stroke({ color: ASOF_YELLOW.stroke, width: 2 }),
    })
}

function setBaseLayerVisibleForDateFilter(visible) {
    const base = olLayerRef.value
    if (!base) return
    if (visible) {
        if (baseLayerVisibleBeforeDateFilter !== null) {
            base.setVisible(baseLayerVisibleBeforeDateFilter)
            baseLayerVisibleBeforeDateFilter = null
        }
    } else {
        if (baseLayerVisibleBeforeDateFilter === null) {
            baseLayerVisibleBeforeDateFilter = base.getVisible()
        }
        base.setVisible(false)
    }
}

async function addAsOfLayer_Vector(geoTable, baseLayer, dateStr, runId) {
    if (!geoTable || !dateStr || !mapRef.value) return
    const asOfGeo = await fetchAllFeatures(geoTable, { date: dateStr, dateDiffOnly: true })
    if (runId !== dateFilterRun || !mapRef.value) return

    const feats = new GeoJSON().readFeatures(asOfGeo, geoJsonReadOptions())
    const source = new VectorSource({ features: feats })
    const dateLabel = formatDisplayDate(dateStr)
    const layer = createAuditOverlayVectorLayer({
        source,
        styleFn: asOfStyleFn,
        label: `na data ${dateLabel}`,
        baseLayer,
        zIndexOffset: 1,
    })
    addOverlayLayerToMap(layer, asOfVectorLayerRef)
}

async function addAsOfLayer_Image(tableName, baseLayer, dateStr, runId) {
    if (!tableName || !dateStr || !mapRef.value) return
    const asOfGeo = await fetchAllFeatures(tableName, { date: dateStr, dateDiffOnly: true })
    if (runId !== dateFilterRun || !mapRef.value) return

    const feats = new GeoJSON().readFeatures(asOfGeo, geoJsonReadOptions())
    const source = new VectorSource({ features: feats })
    const dateLabel = formatDisplayDate(dateStr)
    const layer = createAuditOverlayVectorLayer({
        source,
        styleFn: asOfStyleFn,
        label: `na data ${dateLabel}`,
        baseLayer,
        zIndexOffset: 1,
    })
    addOverlayLayerToMap(layer, asOfVectorLayerRef)
}

async function addAuditLayer_Vector(geoTable, baseLayer, dateStr, runId) {
    if (!geoTable || !dateStr || !mapRef.value) return
    const auditRes = await fetchAllFeatures(geoTable, { date: dateStr, auditChangesOnly: true })
    if (runId !== dateFilterRun || !mapRef.value) return

    const features = (auditRes?.features || []).length
        ? new GeoJSON().readFeatures({ type: 'FeatureCollection', features: auditRes.features }, geoJsonReadOptions())
        : []

    const source = new VectorSource({ features })
    const dateLabel = formatDisplayDate(dateStr)
    const layer = createAuditOverlayVectorLayer({
        source,
        styleFn: auditOverlayStyleFn,
        label: `auditoria ${dateLabel}`,
        baseLayer,
        zIndexOffset: 2,
    })
    if (addOverlayLayerToMap(layer, auditVectorLayerRef)) {
        attachAuditClick(layer)
    }
}

async function addAuditLayer_Image(tableName, baseLayer, dateStr, runId) {
    if (!tableName || !dateStr || !mapRef.value) return
    const auditRes = await fetchAllFeatures(tableName, { date: dateStr, auditChangesOnly: true })
    if (runId !== dateFilterRun || !mapRef.value) return

    const features = (auditRes?.features || []).length
        ? new GeoJSON().readFeatures({ type: 'FeatureCollection', features: auditRes.features }, geoJsonReadOptions())
        : []

    const source = new VectorSource({ features })
    const dateLabel = formatDisplayDate(dateStr)
    const layer = createAuditOverlayVectorLayer({
        source,
        styleFn: auditOverlayStyleFn,
        label: `auditoria ${dateLabel}`,
        baseLayer,
        zIndexOffset: 2,
    })
    if (addOverlayLayerToMap(layer, auditVectorLayerRef)) {
        attachAuditClick(layer)
    }
}

function attachAuditClick(auditLayer) {
    const map = mapRef.value
    if (!map || !auditLayer) return

    if (auditSelectInteractionRef.value) {
        map.removeInteraction(auditSelectInteractionRef.value)
        auditSelectInteractionRef.value = null
    }

    const selectInteraction = new Select({ layers: [auditLayer] })
    selectInteraction.on('select', (event) => {
        const feature = event.selected?.[0] ?? null
        if (!feature) return

        const geom = feature.getGeometry?.()
        const extent = geom?.getExtent?.()
        if (!extent) return
        const center = getCenter(extent)
        activePopoverCoordinate.value = center
        const pixel = map.getPixelFromCoordinate(center)

        const operacao = feature.get('operacao') ?? feature.get('action')
        const user = feature.get('user_banco') ?? feature.get('usuario') ?? feature.get('user_email') ?? ''
        const quando = feature.get('data_ocorrencia') ?? feature.get('created_at') ?? ''

        modalRef.value?.openPopover?.({
            html: `
                <div style="min-width: 220px">
                    <div><b>Ação:</b> ${opLabel(operacao)}</div>
                    <div><b>Usuário:</b> ${String(user || '-')}</div>
                    <div><b>Data:</b> ${formatDateTimeBR(quando)}</div>
                </div>
            `,
        }, pixel)
    })

    auditSelectInteractionRef.value = selectInteraction
    map.addInteraction(selectInteraction)
}

const AUDIT_OP_COLORS = {
    I: { fill: 'rgba(40, 167, 69, 0.45)', stroke: '#1e7e34' },
    U: { fill: 'rgba(0, 123, 255, 0.45)', stroke: '#0056b3' },
    D: { fill: 'rgba(108, 117, 125, 0.5)', stroke: '#495057' },
}
const AUDIT_OP_DEFAULT = { fill: 'rgba(100, 100, 100, 0.4)', stroke: '#555' }

function getAuditOpStyle(feature) {
    const op = String(feature.get('operacao') ?? feature.get('action') ?? '').trim().toUpperCase()
    const mapped = op === 'A' ? 'U' : op === 'E' ? 'D' : op
    return AUDIT_OP_COLORS[mapped] || AUDIT_OP_DEFAULT
}

const auditOverlayStyleFn = (feature) => {
    const { fill, stroke } = getAuditOpStyle(feature)
    const geom = feature.getGeometry?.()
    const type = geom?.getType?.() || 'Polygon'
    if (type === 'Point' || type === 'MultiPoint') {
        return new Style({
            image: new CircleStyle({
                radius: 5,
                fill: new Fill({ color: fill }),
                stroke: new Stroke({ color: stroke, width: 1.5 }),
            }),
        })
    }
    if (type === 'LineString' || type === 'MultiLineString') {
        return new Style({ stroke: new Stroke({ color: stroke, width: 2 }) })
    }
    return new Style({
        fill: new Fill({ color: fill }),
        stroke: new Stroke({ color: stroke, width: 1.5 }),
    })
}

function isRasterLayer(layer) {
    return layer instanceof ImageLayer || layer instanceof TileLayer
}

function waitForMapRender(map, timeoutMs = 15000) {
    return new Promise((resolve) => {
        if (!map) {
            resolve()
            return
        }
        let finished = false
        const done = () => {
            if (finished) return
            finished = true
            map.un('rendercomplete', onComplete)
            clearTimeout(timer)
            resolve()
        }
        const onComplete = () => done()
        const timer = setTimeout(done, timeoutMs)
        map.once('rendercomplete', onComplete)
        map.render()
    })
}

async function applyDateFilter() {
    if (!filterDate.value || !olLayerRef.value || !layerConfigRef.value) return
    const selectedDate = filterDate.value
    const layer = olLayerRef.value
    const config = layerConfigRef.value
    const tableName = resolveDateFilterTableName(config, layer)
    const runId = ++dateFilterRun
    applyingDate.value = true
    mapDateFilterLoading.value = true
    await nextTick()
    try {
        removeDateFilterOverlay()
        if (runId !== dateFilterRun || !mapRef.value) return

        if (isVectorLayer(layer)) {
            await addAsOfLayer_Vector(config.geo_table, layer, selectedDate, runId)
            if (runId !== dateFilterRun || !mapRef.value) return
            await addAuditLayer_Vector(config.geo_table, layer, selectedDate, runId)
        } else if (isRasterLayer(layer)) {
            await addAsOfLayer_Image(tableName, layer, selectedDate, runId)
            if (runId !== dateFilterRun || !mapRef.value) return
            await addAuditLayer_Image(tableName, layer, selectedDate, runId)
        }

        if (runId !== dateFilterRun || !mapRef.value) return
        dateFilterTableNameRef.value = tableName
        attachAsOfClick(tableName)
        showAuditLegend.value = true
        await waitForMapRender(mapRef.value)
    } finally {
        if (runId === dateFilterRun) {
            applyingDate.value = false
            mapDateFilterLoading.value = false
        }
    }
}

async function clearDateFilter() {
    if (!olLayerRef.value || !layerConfigRef.value) return
    dateFilterRun++
    filterDate.value = null
    applyingDate.value = true
    try {
        removeDateFilterOverlay()
        setBaseLayerVisibleForDateFilter(true)
    } finally {
        applyingDate.value = false
    }
}

function resolveDateFilterTableName(config, layer) {
    if (isVectorLayer(layer)) {
        return config?.geo_table ?? null
    }
    return tableNameFromGeoServerLayer(config?.geo_server_layer)
}

function featurePkValue(feature) {
    const gid = feature.get('gid') ?? feature.get('pk_value')
    if (gid != null && gid !== '') return String(gid)
    const id = feature.getId()
    if (id != null && id !== '') return String(id)
    return null
}

function auditTimelineDotColor(op) {
    const x = String(op ?? '').trim().toUpperCase()
    if (x === 'I') return '#1e7e34'
    if (x === 'U' || x === 'A') return '#0056b3'
    if (x === 'D' || x === 'E') return '#495057'
    return '#888'
}

function buildAuditTimelineHtml(pk, events) {
    if (!events.length) {
        return `
            <div style="min-width:260px">
                <div><b>Feição:</b> ${pk}</div>
                <div style="margin-top:8px;color:#888">Nenhum registro de auditoria.</div>
            </div>
        `
    }

    const items = events.map((ev, index) => {
        const op = opLabel(ev.operacao ?? ev.action)
        const color = auditTimelineDotColor(ev.operacao ?? ev.action)
        const isLast = index === events.length - 1
        const line = isLast
            ? ''
            : '<div style="flex:1;width:2px;background:#e8e8e8;min-height:12px"></div>'

        return `
            <div style="display:flex;gap:12px;margin-bottom:${isLast ? '0' : '12px'}">
                <div style="display:flex;flex-direction:column;align-items:center;width:14px">
                    <div style="width:10px;height:10px;border-radius:50%;background:${color};margin-top:4px"></div>
                    ${line}
                </div>
                <div style="flex:1;padding-bottom:4px">
                    <div style="font-weight:600">${op}</div>
                    <div style="font-size:12px;color:#555">${formatDateTimeBR(ev.created_at ?? ev.data_ocorrencia)}</div>
                    <div style="font-size:12px">${String(ev.user_email ?? ev.usuario ?? '-')}</div>
                    ${ev.application ? `<div style="font-size:11px;color:#888">${String(ev.application)}</div>` : ''}
                </div>
            </div>
        `
    }).join('')

    return `
        <div style="min-width:280px;max-width:360px">
            <div style="margin-bottom:10px;font-weight:600">Histórico da feição ${pk}</div>
            <div>${items}</div>
        </div>
    `
}

async function handleAsOfFeatureClick(pixel) {
    const map = mapRef.value
    const tableName = dateFilterTableNameRef.value
    if (!map || !tableName || !asOfVectorLayerRef.value?.getVisible?.()) {
        return false
    }

    const feature = getAsOfFeatureAtPixel(map, pixel)
    if (!feature) return false

    const pk = featurePkValue(feature)
    if (!pk) return false

    const geom = feature.getGeometry?.()
    const extent = geom?.getExtent?.()
    if (!extent) return false

    const center = getCenter(extent)
    activePopoverCoordinate.value = center
    const popoverPixel = map.getPixelFromCoordinate(center)

    modalRef.value?.openPopover?.({
        html: '<div style="min-width:260px">Carregando histórico...</div>',
    }, popoverPixel)

    try {
        const { data } = await axios.get(
            `/api/maps/layers/${encodeURIComponent(tableName)}/audit-history`,
            { params: { pk } },
        )
        const events = data?.data ?? []
        modalRef.value?.openPopover?.({
            html: buildAuditTimelineHtml(pk, events),
        }, popoverPixel)
    } catch {
        modalRef.value?.openPopover?.({
            html: '<div style="min-width:260px;color:#cf1322">Não foi possível carregar o histórico.</div>',
        }, popoverPixel)
    }

    return true
}

function attachAsOfClick(tableName) {
    const map = mapRef.value
    if (!map || !tableName || !asOfVectorLayerRef.value) return

    dateFilterTableNameRef.value = tableName

    if (asOfClickHandler) {
        map.un('singleclick', asOfClickHandler)
        asOfClickHandler = null
    }

    asOfClickHandler = async (evt) => {
        await handleAsOfFeatureClick(evt.pixel)
    }

    map.on('singleclick', asOfClickHandler)
}

// Nome da tabela a partir do nome qualificado da camada no GeoServer (ex: tramandai:geo_lote -> geo_lote)
function tableNameFromGeoServerLayer(geoServerLayer) {
    if (!geoServerLayer || typeof geoServerLayer !== 'string') return null
    const parts = geoServerLayer.split(':')
    return parts.length > 1 ? parts[1] : parts[0]
}

function opLabel(op) {
    const x = String(op ?? '').trim().toUpperCase()
    if (x === 'I') return 'Inclusão'
    if (x === 'U' || x === 'A') return 'Alteração'
    if (x === 'D' || x === 'E') return 'Exclusão'
    return x || 'Operação'
}

function formatDateTimeBR(value) {
    if (!value) return '-'
    const s = String(value)
    const d = new Date(s)
    if (Number.isNaN(d.getTime())) return s
    return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(d)
}

// ======= Fallback de estilo (se layer não tiver style definido) =======
const _defaultStyleCache = {
    Point: new Style({
        image: new CircleStyle({
            radius: 5,
            fill: new Fill({ color: 'rgba(0,0,255,0.5)' }),
            stroke: new Stroke({ color: '#0044cc', width: 1.5 }),
        }),
    }),
    LineString: new Style({ stroke: new Stroke({ color: '#008000', width: 2 }) }),
    Polygon: new Style({
        fill: new Fill({ color: 'rgba(255,165,0,0.35)' }),
        stroke: new Stroke({ color: '#ff8c00', width: 1.5 }),
    }),
    MultiPoint: null, MultiLineString: null, MultiPolygon: null,
}
_defaultStyleCache.MultiPoint = _defaultStyleCache.Point
_defaultStyleCache.MultiLineString = _defaultStyleCache.LineString
_defaultStyleCache.MultiPolygon = _defaultStyleCache.Polygon

function defaultStyleFn(feature/*, res */) {
    const geom = feature.getGeometry?.()
    const type = geom?.getType?.() || 'Polygon'
    return _defaultStyleCache[type] || _defaultStyleCache.Polygon
}

// ======= Checagem: é camada vetorial (VectorLayer + VectorSource)? =======
function isVectorLayer(layer) {
    if (!layer) return false
    const src = layer.getSource?.()
    return (layer instanceof VectorLayer) && (src instanceof VectorSource)
}

function substituteAttributeTemplate(template, props) {
    if (!template) return ''
    const p = props && typeof props === 'object' ? props : {}
    return String(template).replace(/\$([a-zA-Z0-9_]+)/g, (_, key) => (p[key] != null ? String(p[key]) : ''))
}

/** Limpeza Personalizado: Select (vetor) ou singleclick (imagem) ao sair da página */
let disposePersonalizado = null

// ======= Mapa =======
onMounted(async () => {
    let layer = await getLayer()
    layerConfigRef.value = layer

    if (isThematicLayer.value) {
        layer.layer_type = 'VectorLayer'
        layer.layer_source = 'Vector'
        layer.geo_table = 'thematic_maps.thematic_' + layerId.value
    }

    applyViewerLayerSwitcherDefaults(layer)

    layer.style = buildStyleFunctionFromBackend(layer)
    const layerOpenLayers = await createLayerToViewer(layer)
    olLayerRef.value = layerOpenLayers

    if (!layerOpenLayers) {
        console.error(
            'Não foi possível criar a camada no mapa.',
            { type: mapsLayerTypeValue(layer), name: layer?.name, geo_table: layer?.geo_table, geo_server_layer: layer?.geo_server_layer }
        )
        return
    }

    // Define flag para exibir (ou não) o painel de filtros
    isVectorLayerFlag.value = isVectorLayer(layerOpenLayers)

    // Verifica se a camada suporta filtro por data (audit.layers_edit_audits)
    if ((mapsLayerTypeValue(layer) === 'ImageLayer' || mapsLayerTypeValue(layer) === 'TileLayer') && layer.geo_server_layer) {
        const tableName = tableNameFromGeoServerLayer(layer.geo_server_layer)
        if (tableName) {
            try {
                const { data } = await axios.get(`/api/maps/layers/${tableName}/audit-dates`)
                if (data?.data?.length > 0) hasAudit.value = true
            } catch (_) {}
        }
    } else if (layer.geo_table) {
        try {
            const { data } = await axios.get(`/api/maps/layers/${layer.geo_table}/audit-dates`)
            if (data?.data?.length > 0) hasAudit.value = true
        } catch (_) {}
    }

    const map = new Map({
        target: 'map',
        layers: [baseLayers()],
        view: new View(getDefaultMapView()),
        controls: defaultControlsOL().extend([fullScreenBtn]),
    })

    if (isThematicLayer.value) {
        const selectInteraction = new Select({ layers: [layerOpenLayers] })
        selectInteraction.on('select', (event) => {
            const feature = event.selected?.[0] ?? null
            if (!feature) return

            let props = feature.getProperties()
            delete props.geometry

            const layer = event.target.getLayer(feature)
            const template = layer?.get('reference') || ''

            const reference = template.replace(/\$([a-zA-Z0-9_]+)/g, (match, key) => props[key] ?? '')
            feature.set('reference', reference)

            openDetailsByFeatureTematic(feature)
        })
        map.addLayer(layerOpenLayers)
        map.addInteraction(selectInteraction)

        map.on('moveend', () => {
            if (activePopoverCoordinate?.value && modalRef?.value?.updatePopoverPosition) {
                const pixel = map.getPixelFromCoordinate(activePopoverCoordinate.value)
                modalRef.value.updatePopoverPosition(pixel)
            }
        })
    } else {
        map.addLayer(layerOpenLayers)

        const templateHtml = layer.attribute_display_html
        const isPersonalizado =
            String(layer.action ?? '') === 'Personalizado' && templateHtml && String(templateHtml).trim().length > 0

        if (isPersonalizado) {
            const onMoveEndForPopover = () => {
                if (activePopoverCoordinate?.value && modalRef?.value?.updatePopoverPosition) {
                    const pixel = map.getPixelFromCoordinate(activePopoverCoordinate.value)
                    modalRef.value.updatePopoverPosition(pixel)
                }
            }

            const openHtmlPopoverFromTemplate = (props, coordinate) => {
                const template = olLayerRef.value?.get('reference') || templateHtml
                const html = substituteAttributeTemplate(template, props)
                if (!html) {
                    modalRef.value?.closePopover?.()
                    activePopoverCoordinate.value = null
                    return
                }
                activePopoverCoordinate.value = coordinate
                const pixel = map.getPixelFromCoordinate(coordinate)
                modalRef.value?.openPopover?.(
                    { html: html.replaceAll('\n', '<br/>') },
                    pixel
                )
            }

            const isVectorConfig = mapsLayerTypeValue(layer) === 'VectorLayer'

            if (isVectorConfig) {
                const selectPersonalizado = new Select({ layers: [layerOpenLayers] })
                selectPersonalizado.on('select', async (event) => {
                    const pixel = event.mapBrowserEvent?.pixel
                    if (pixel && await handleAsOfFeatureClick(pixel)) {
                        event.target.getFeatures().clear()
                        return
                    }
                    const feature = event.selected?.[0] ?? null
                    if (!feature) {
                        modalRef.value?.closePopover?.()
                        activePopoverCoordinate.value = null
                        return
                    }
                    const gid = Number(feature.get('gid'))
                    if (!Number.isFinite(gid) || gid < 1) {
                        modalRef.value?.closePopover?.()
                        activePopoverCoordinate.value = null
                        return
                    }
                    try {
                        const { data: resp } = await axios.post(
                            `/api/maps/layers/${layerId.value}/feature-by-gid`,
                            { gid }
                        )
                        const props = resp?.data
                        if (!props || typeof props !== 'object') {
                            modalRef.value?.closePopover?.()
                            activePopoverCoordinate.value = null
                            return
                        }
                        const geom = feature.getGeometry()
                        const center = geom ? getCenter(geom.getExtent()) : map.getView().getCenter()
                        openHtmlPopoverFromTemplate(props, center)
                    } catch (e) {
                        console.warn('Consulta por gid (Personalizado) falhou:', e)
                        modalRef.value?.closePopover?.()
                        activePopoverCoordinate.value = null
                    }
                })
                map.addInteraction(selectPersonalizado)
                map.on('moveend', onMoveEndForPopover)

                disposePersonalizado = () => {
                    map.removeInteraction(selectPersonalizado)
                    map.un('moveend', onMoveEndForPopover)
                }
            } else {
                const onSingleClickPersonalizado = async (evt) => {
                    if (await handleAsOfFeatureClick(evt.pixel)) {
                        return
                    }
                    const [lon, lat] = toLonLat(evt.coordinate)
                    try {
                        const { data: resp } = await axios.post(
                            `/api/maps/layers/${layerId.value}/feature-at-point`,
                            { lon, lat }
                        )
                        const props = resp?.data
                        if (!props || typeof props !== 'object') {
                            modalRef.value?.closePopover?.()
                            activePopoverCoordinate.value = null
                            return
                        }
                        openHtmlPopoverFromTemplate(props, evt.coordinate)
                    } catch (e) {
                        console.warn('Consulta de atributos (Personalizado) falhou:', e)
                        modalRef.value?.closePopover?.()
                        activePopoverCoordinate.value = null
                    }
                }

                map.on('singleclick', onSingleClickPersonalizado)
                map.on('moveend', onMoveEndForPopover)

                disposePersonalizado = () => {
                    map.un('singleclick', onSingleClickPersonalizado)
                    map.un('moveend', onMoveEndForPopover)
                }
            }
        }
    }

    mapRef.value = map

    // Só tenta descobrir campos e preparar filtro se for vetorial
    if (isVectorLayerFlag.value) {
        await discoverFieldsFromLayer(layerOpenLayers)
        if (fields.value.length) addRow()
    }
})

const closePopover = () => {
    // fechar popover se precisar
}

const openDetailsByFeatureTematic = (feature) => {
    if (!feature) {
        modalRef.value?.closePopover()
        activePopoverCoordinate.value = null
        return
    }
    const geometry = feature.getGeometry()
    const extent = geometry.getExtent()
    const center = getCenter(extent)
    activePopoverCoordinate.value = center

    const pixel = mapRef.value.getPixelFromCoordinate(center)
    const reference = feature.get('reference')

    modalRef.value.openPopover(
        { html: reference ? reference.replaceAll('\n', '<br/>') : null },
        pixel
    )
}

const getLayer = async () => {
    const prefix = isThematicLayer.value ? 'thematic-layers' : 'layers'
    const { data } = await window.axios.get(`/api/maps/${prefix}/${layerId.value}`)
    return data.data
}

// ======= Descobrir campos =======
async function discoverFieldsFromLayer(layer) {
    const src = layer.getSource?.()
    if (!src) return

    const ensureLoaded = () =>
        new Promise((resolve) => {
            const tryNow = () => {
                const feats = src.getFeatures?.() || []
                if (feats.length > 0) return resolve()
                let resolved = false
                const onEnd = () => { if (!resolved) { resolved = true; resolve() } }
                src.once?.('featuresloadend', onEnd)
                src.once?.('change', () => { if ((src.getFeatures?.() || []).length > 0) onEnd() })
                setTimeout(() => onEnd(), 800)
            }
            tryNow()
        })

    await ensureLoaded()

    const feats = src.getFeatures?.() || []
    const sampleProps = {}
    for (const f of feats) {
        const p = f.getProperties()
        for (const k of Object.keys(p || {})) {
            if (k === 'geometry') continue
            if (sampleProps[k] == null && p[k] != null) sampleProps[k] = p[k]
        }
    }

    const guessed = Object.keys(sampleProps).map((k) => {
        const v = sampleProps[k]
        const t = guessType(v)
        return { key: k, label: k, type: t }
    })

    // inclui campos só-nulos
    for (const f of feats) {
        const p = f.getProperties()
        for (const k of Object.keys(p || {})) {
            if (k === 'geometry') continue
            if (!guessed.find(g => g.key === k)) {
                guessed.push({ key: k, label: k, type: 'string' })
            }
        }
    }

    guessed.sort((a, b) => a.key.localeCompare(b.key))
    fields.value = guessed
}

function guessType(v) {
    if (v == null) return 'string'
    if (typeof v === 'number') return 'number'
    if (typeof v === 'string') {
        const isoLike = /^\d{4}-\d{2}-\d{2}/.test(v)
        if (isoLike && !isNaN(Date.parse(v))) return 'date'
        if (!isNaN(Number(v)) && v.trim() !== '') return 'number'
        return 'string'
    }
    if (v instanceof Date) return 'date'
    return 'string'
}

// ======= Aplicar/Limpar filtro =======
function applyFilter() {
    if (!isVectorLayerFlag.value) return // não faz nada se não for vetorial

    const layer = olLayerRef.value
    if (!layer) return
    const src = layer.getSource?.()
    if (!src) return
    applying.value = true

    // guarda o estilo original uma única vez
    if (!layer.get('originalStyle')) {
        layer.set('originalStyle', layer.getStyle?.() ?? null) // pode ser null
    }
    const originalStyle = layer.get('originalStyle')

    // normaliza para função, usando fallback se originalStyle === null
    const originalStyleFn = originalStyle
        ? (typeof originalStyle === 'function' ? originalStyle : (f,r)=>originalStyle)
        : defaultStyleFn

    const predicate = buildPredicate()

    layer.setStyle((feature, res) => {
        return predicate(feature) ? originalStyleFn(feature, res) : null
    })

    // contador
    let ok = 0
    const feats = src.getFeatures?.() || []
    for (const f of feats) if (predicate(f)) ok++
    matchCount.value = ok

    layer.changed()
    applying.value = false
}

function clearFilter() {
    if (!isVectorLayerFlag.value) return // não faz nada se não for vetorial

    filterRows.value = []
    matchCount.value = null

    const layer = olLayerRef.value
    if (!layer) return

    const originalStyle = layer.get('originalStyle') // pode ser null
    if (originalStyle) {
        layer.setStyle(originalStyle)
    } else {
        // se nunca houve estilo original explícito, volta pro fallback
        layer.setStyle(defaultStyleFn)
    }
    layer.changed()

    if (fields.value.length) addRow()
}

function buildPredicate() {
    const rules = filterRows.value
        .filter(r => r.field && (isEmptyOp(r) || needsBetween(r) || needsSingleInput(r)))
        .map(r => ({ ...r }))

    return (feature) => {
        const props = feature.getProperties?.() || {}
        for (const r of rules) {
            const t = fieldTypeOf(r.field)
            const val = props[r.field]

            if (t === 'string') {
                const s = (val ?? '').toString()
                const needle = (r.value ?? '').toString()
                const SS = s.toLowerCase()
                const NN = needle.toLowerCase()
                if (r.op === 'contains'     && !SS.includes(NN)) return false
                if (r.op === 'not_contains' &&  SS.includes(NN)) return false
                if (r.op === 'eq'           &&  SS !== NN)       return false
                if (r.op === 'starts'       && !SS.startsWith(NN)) return false
                if (r.op === 'ends'         && !SS.endsWith(NN))   return false
                if (r.op === 'empty'        &&  s.trim() !== '')  return false
                if (r.op === 'not_empty'    &&  s.trim() === '')  return false
            }

            if (t === 'number') {
                const n = Number(val)
                if (isNaN(n)) return false
                if (r.op === 'eq'      && !(n === Number(r.value))) return false
                if (r.op === 'neq'     && !(n !== Number(r.value))) return false
                if (r.op === 'gt'      && !(n >  Number(r.value))) return false
                if (r.op === 'gte'     && !(n >= Number(r.value))) return false
                if (r.op === 'lt'      && !(n <  Number(r.value))) return false
                if (r.op === 'lte'     && !(n <= Number(r.value))) return false
                if (r.op === 'between') {
                    const a = Number(r.valueMin), b = Number(r.valueMax)
                    if (isNaN(a) || isNaN(b)) return false
                    const min = Math.min(a, b), max = Math.max(a, b)
                    if (!(n >= min && n <= max)) return false
                }
            }

            if (t === 'date') {
                const d = toDate(val); if (!d) return false
                if (r.op === 'on') {
                    const target = toDate(r.value); if (!target) return false
                    if (!sameDay(d, target)) return false
                }
                if (r.op === 'before') {
                    const target = toDate(r.value); if (!target) return false
                    if (!(d < addOneDay(target))) return false
                }
                if (r.op === 'after') {
                    const target = toDate(r.value); if (!target) return false
                    if (!(d >= addOneDay(target))) return false
                }
                if (r.op === 'between') {
                    const a = toDate(r.valueMin), b = toDate(r.valueMax)
                    if (!a || !b) return false
                    const start = minDate(startOfDay(a), startOfDay(b))
                    const end   = maxDate(endOfDay(a), endOfDay(b))
                    if (!(d >= start && d <= end)) return false
                }
            }
        }
        return true
    }
}

// helpers de data
function toDate(v) {
    if (!v) return null
    if (v instanceof Date) return v
    const d = new Date(v)
    return isNaN(d.getTime()) ? null : d
}
function startOfDay(d) { const x = new Date(d); x.setHours(0,0,0,0); return x }
function endOfDay(d)   { const x = new Date(d); x.setHours(23,59,59,999); return x }
function addOneDay(d)  { const x = new Date(d); x.setDate(x.getDate()+1); x.setHours(0,0,0,0); return x }
function sameDay(a,b){ return a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate() }
function minDate(a,b){ return a < b ? a : b }
function maxDate(a,b){ return a > b ? a : b }

onUnmounted(() => {
    dateFilterRun++
    mapDateFilterLoading.value = false
    applyingDate.value = false
    disposePersonalizado?.()
    disposePersonalizado = null
    removeDateFilterOverlay()
    setBaseLayerVisibleForDateFilter(true)
    const map = mapRef.value
    if (map) {
        map.setTarget(null)
        mapRef.value = null
    }
})
</script>

<style scoped>
.map-wrapper { width: 100%; height: 100vh; position: relative; }
.map { width: 100%; height: 100%; border-radius: 8px; overflow: hidden; }

.map-loading-overlay {
    position: absolute;
    inset: 0;
    z-index: 20;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.5);
    pointer-events: all;
}

/* Painel flutuante */
.filter-panel {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 420px;
    max-width: calc(100% - 24px);
    z-index: 10;
    box-shadow: 0 8px 24px rgba(0,0,0,0.18);
    border-radius: 10px;
}

.panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
}
.title { font-weight: 600; }
.muted { color: #888; }

.filter-row {
    padding: 6px 0;
    border-bottom: 1px dashed #eee;
}
.filter-row:last-child { border-bottom: 0; }

.panel-actions {
    margin-top: 8px;
    display: flex;
    justify-content: flex-end;
}

/* Painel "como na data" à esquerda */
.date-filter-panel {
    left: 12px;
    right: auto;
    width: 320px;
}
.date-filter-row {
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.date-filter-row .date-actions {
    display: flex;
    justify-content: flex-end;
}
.date-filter-row .small {
    font-size: 12px;
}

.audit-legend {
    position: absolute;
    left: 12px;
    bottom: 12px;
    width: 220px;
    z-index: 10;
    box-shadow: 0 8px 24px rgba(0,0,0,0.18);
    border-radius: 10px;
}
.audit-legend .panel-header { margin-bottom: 8px; }
.audit-legend-items {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.audit-legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
}
.audit-legend-color {
    width: 18px;
    height: 18px;
    border-radius: 3px;
    border: 2px solid;
    flex-shrink: 0;
}
</style>
